/**
 * NotificationRow — one row in the NotificationPanel.
 *
 * Styled by state_class via dynamic atoms (notif-row-failure, notif-row-info,
 * notif-row-action-required) defined in @layer components in index.css.
 * ADR-034: these dynamic atoms are guarded by styling-emission.test.ts.
 *
 * Tap-to-action: rows with a non-null action deep-link via action.kind +
 * action.target_id. Rows with null action are not tappable.
 *
 * markRead is called on tap (both navigation and non-navigation taps).
 */

import { useNavigate } from "react-router-dom";
import { useMarketplaceEnabled } from "../../hooks/useMarketplaceEnabled";
import type { NotificationItem } from "../../lib/api";
import type { NotificationTone } from "../../lib/notification-display";
import {
  buttonForType,
  chipForType,
  routeForAction,
  toneForClass,
  labelForClass,
} from "../../lib/notification-display";

// Tone → icon character (rendered as text for zero-dep accessibility).
const TONE_ICON: Record<NotificationTone, string> = {
  failure: "⚠",
  action: "✦",
  info: "●",
};

interface NotificationRowProps {
  item: NotificationItem;
  onMarkRead: (id: string) => void;
  variant?: "panel" | "log";
}

export function NotificationRow({
  item,
  onMarkRead,
  variant = "panel",
}: NotificationRowProps) {
  const navigate = useNavigate();
  const marketplaceEnabled = useMarketplaceEnabled();
  const tone = toneForClass(item.state_class);
  const isRead = item.read_state === "read";
  const actionRoute = item.action
    ? routeForAction(
        item.action.kind,
        item.action.target_id,
        marketplaceEnabled,
      )
    : null;
  const isTappable = actionRoute !== null;
  const isRowInteractive = isTappable || (variant === "log" && !isRead);

  // Format relative time.
  const relTime = formatRelTime(item.created_at);
  const absoluteTime = formatAbsoluteTime(item.created_at);
  const logChip =
    item.state_class === "action_required" ? chipForType(item.type) : undefined;
  // The panel chip prefers the per-type chip ("New request", "Draft
  // waiting") so a request or payment row is not mislabelled as a draft; the
  // generic class label is only the fallback.
  const panelChip = logChip ?? labelForClass(item.state_class);
  const logButton = actionRoute ? buttonForType(item.type) : undefined;

  function handleAction() {
    if (!isRead) onMarkRead(item.id);
    if (actionRoute) {
      navigate(actionRoute);
    }
  }

  function handleMarkRead() {
    onMarkRead(item.id);
  }

  const rowClass = [
    // Base layout
    "notif-row-base",
    // Dynamic tone atom — notif-row-${tone}
    `notif-row-${tone}`,
    isRead ? "notif-row-read" : "notif-row-unread",
    isRowInteractive ? "cursor-pointer" : "cursor-default",
  ].join(" ");

  const statusContent = (
    <>
      {variant === "panel" && (
        <span className={`notif-class-chip notif-chip-${tone}`}>
          {panelChip}
        </span>
      )}
      {variant === "log" && item.state_class === "failure" && (
        <span className="notif-class-chip notif-chip-failure">
          Needs attention
        </span>
      )}
      {variant === "log" && logChip && (
        <span className="notif-class-chip notif-chip-action">{logChip}</span>
      )}
      {variant === "panel" && !isRead && (
        <span className="notif-unread-dot" aria-label="Unread" />
      )}
    </>
  );

  const bodyContent = (
    <div className="notif-row-body min-w-0 flex-1">
      <div className="flex items-start justify-between gap-2">
        <div className="notif-row-heading flex min-w-0 items-center gap-2">
          <p className="notif-row-title">{item.content.title}</p>
          {variant === "log" && !isRead && (
            <span className="notif-unread-dot" aria-label="Unread" />
          )}
        </div>
        <time
          className="notif-row-time shrink-0"
          dateTime={item.created_at}
          aria-label={`${relTime}; ${absoluteTime}`}
        >
          {variant === "log" ? `${relTime} · ${absoluteTime}` : relTime}
        </time>
      </div>
      <p className="notif-row-text">{item.content.body}</p>
      {(variant === "panel" || item.state_class === "failure" || logChip) && (
        <div className="mt-1.5 flex items-center gap-2">{statusContent}</div>
      )}
    </div>
  );

  const leadingContent = (
    <>
      <span className="notif-row-accent" aria-hidden />
      <span className={`notif-row-icon notif-icon-${tone}`} aria-hidden>
        {TONE_ICON[tone]}
      </span>
    </>
  );

  const rowContent = (
    <>
      {leadingContent}
      {bodyContent}
    </>
  );

  if (variant === "panel" && !isTappable) {
    return <div className={rowClass}>{rowContent}</div>;
  }

  return (
    <div className={rowClass}>
      {variant === "log" ? (
        <>
          {leadingContent}
          <div className="notif-log-stack flex min-w-0 flex-1 flex-col items-start">
            {isRowInteractive ? (
              <button
                type="button"
                className="notif-row-main"
                onClick={handleAction}
              >
                {bodyContent}
              </button>
            ) : (
              <div className="notif-row-main">{bodyContent}</div>
            )}
            {(logButton || !isRead) && (
              <div className="notif-log-actions mt-2.5 flex flex-wrap items-center gap-2">
                {logButton && (
                  <button
                    type="button"
                    className="notif-log-cta"
                    onClick={handleAction}
                  >
                    {logButton}
                  </button>
                )}
                {!isRead && (
                  <button
                    type="button"
                    className="min-h-10 rounded-lg px-3 text-xs font-semibold text-fg-muted transition-[background-color,color,scale] duration-150 hover:bg-[var(--pro-soft2)] hover:text-[var(--sill-fg)] active:scale-[0.96]"
                    onClick={handleMarkRead}
                  >
                    Mark as read
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <button
          type="button"
          className="notif-row-main"
          onClick={handleAction}
        >
          {rowContent}
        </button>
      )}
    </div>
  );
}

function formatRelTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const secs = Math.floor(ms / 1000);
    if (secs < 60) return "just now";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}

function formatAbsoluteTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
